import { ChangeEvent, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { MESSAGES } from '@app/constant';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateImageMutation, useDeleteImageMutation } from '@modules/images';
import { useCreateMarketMutation, useLazyGetMarketByIdQuery, useUpdateMarketMutation } from '@modules/market';
import { useRenderFileViewHook } from '@app/hooks/imagesRender';

import { IFlowProcess, IMarketFormData, OnChangeFunctionProps } from './helper/types';
import { createMarkSchema, initialFlowState, initialFormState } from './helper/data';
import { onChangeUpload, handleDeleteImage } from './helper/localHelper';
import { prepareClonedData, prepareImageUploadPromises } from './helper/utils';

export const useMarketLogic = () => {
  const { id } = useParams()

  const [createMarket] = useCreateMarketMutation()
  const [updateMarket] = useUpdateMarketMutation()
  const [createImage] = useCreateImageMutation()
  const [deleteImage] = useDeleteImageMutation()
  const [getMarketById] = useLazyGetMarketByIdQuery();

  const [flowProcess, setFlowProcess] = useState<IFlowProcess>(initialFlowState)

  const methods = useForm<IMarketFormData>({
    // @ts-ignore
    resolver: yupResolver(createMarkSchema),
    mode: 'onSubmit',
    defaultValues: initialFormState
  });

  const {
    reset,
    watch,
    register,
    setValue,
    setError,
    handleSubmit,
    formState: { errors }
  } = methods;

  const market = watch('market')
  const images = watch('uploadedFileList')
  const existingImagesWatch = watch('existingImages')

  const previews = useRenderFileViewHook(images, existingImagesWatch)

  const handleLoadingState = (isLoading: boolean, successMessage: string = '') => {
    setFlowProcess((prev) => ({
      ...prev,
      isButtonLoading: isLoading,
      isSuccess: successMessage,
      largeImageMessage: '',
    }));
  };

  const onSubmit = async (data: IMarketFormData) => {
    handleLoadingState(true);

    let imagePaths: string[] = [];

    try {
      const imageUploadPromises = prepareImageUploadPromises(data.uploadedFileList, createImage);

      const uploadedImages = await Promise.all(imageUploadPromises || []);

      imagePaths = uploadedImages?.map((result) => result?.id || '');

      const clonedData = prepareClonedData(data, imagePaths);

      if (id) {
        await updateMarket({ id, body: clonedData }).unwrap()
        getMarketData()
      } else {
        await createMarket(clonedData).unwrap();
        reset(initialFormState);
      }

      handleLoadingState(false, id
        ? MESSAGES.marketHasBeenSuccessfullyUpdated
        : MESSAGES.marketHasBeenSuccessfullyCreated);
    } catch (error: any) {
      if (imagePaths?.length) {
        // If after first step, when FileList uploaded in backend.
        // And then before market creation appears some problems.
        // In that case we delete all uploaded images

        imagePaths.forEach(async (imageId) => {
          await deleteImage({ id: imageId })
        })
      }

      handleLoadingState(false, error?.data?.message || MESSAGES.somethingWentWrong);
    }
  };

  const getMarketData = async () => {
    if (!id) {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))

      return
    }

    try {
      const gotMarket = await getMarketById(id).unwrap()

      reset({
        ...gotMarket,
        existingImages: gotMarket.images,
      });
    } catch (error) {
      console.log("Error", error);
    } finally {
      setFlowProcess((prev) => ({ ...prev, isLoading: false }))
    }
  }

  useEffect(() => {
    getMarketData()
  }, [id])

  return {
    ...flowProcess,
    market,
    paramId: id,
    methods,
    errors,
    watch,
    setValue,
    setError,
    setFlowProcess,
    onSubmit,
    register,
    handleSubmit,
    handleDeleteImage: (bas64: string) => handleDeleteImage({
      bas64,
      previews,
      images,
      watch,
      setValue,
      setState: setFlowProcess,
    }),
    onChangeUpload: (props: OnChangeFunctionProps) => onChangeUpload({
      watch,
      setValue,
      setState: setFlowProcess,
      setError,
      ...props,
    }),
  }
}
